import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, Button, TextField, Box, Typography, Grid, MenuItem, Avatar, IconButton } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import moment from "moment/moment";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import Select from "react-select";
import { useUpdateMilestoneMutation } from "store/slices/apis/milestoneApiSlice";
import CalenderSVG from "assets/logo/Calendar";
import { RxCross2 } from "react-icons/rx";

const milestoneStatusOptions = [
    { label: "Completed", value: "Completed" },
    { label: "In Progress", value: "In Progress" },
    { label: "Future", value: "Future" },
];

const rightLabelStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
};

const rightTextStyle = {
    "& .MuiInputBase-input": {
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "100%",
        letterSpacing: "0.16px",
        color: "#0A0A0C",
        fontFamily: "Outfit, sans-serif",
    },
};

const labelTextStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
    mb: 1,
    mt: 1,
};

const EditMilestone = (props) => {
    const { open, handleClose, rowData } = props;
    const { projectId } = useParams();
    const dispatch = useDispatch();

    const [updateMilestone, { isLoading }] = useUpdateMilestoneMutation();
    const [tabValue, setTabValue] = useState(0);

    const [formData, setFormData] = useState({
        milestone: rowData?.milestone || "",
        milestoneValue: rowData?.milestoneValue || null,
        dueDate: rowData?.dueDate
            ? moment.utc(rowData.dueDate).format("YYYY-MM-DD")
            : "",
        curePeriodDueDate: rowData?.curePeriodDueDate
            ? moment.utc(rowData.curePeriodDueDate).format("YYYY-MM-DD")
            : null,
        status: rowData?.status || "",
        projectId: projectId,
    });

    const validationSchema = Yup.object({
        milestone: Yup.string().required("Milestone Name field is required"),
        milestoneValue: Yup.number()
            .typeError("Milestone Value must be a number")
            .nullable(),
        dueDate: Yup.date().required("Due Date field is required"),
        status: Yup.string().required("Status field is required"),
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {

        let reqBody = {
            activityId: rowData?.activityId,
            projectId: projectId,
            milestone: values?.milestone,
            milestoneValue: values?.milestoneValue !== "" && values?.milestoneValue !== null
                ? Number(values.milestoneValue)
                : 0,
            dueDate: moment.utc(values?.dueDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]'),
            curePeriodDueDate: values?.curePeriodDueDate
                ? moment.utc(values?.curePeriodDueDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            status: values?.status,
            currentRunRate: 0,
        };

        try {
            const response = await updateMilestone({ body: reqBody }).unwrap();
            openNotification("Milestone updated successfully", "success");
            resetForm();
            setSubmitting(false);
            handleClose();
        } catch (error) {
            openNotification(error?.error ?? "Something went wrong", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <Dialog open={open}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: { borderTopLeftRadius: "5px", borderTopRightRadius: "5px", backgroundColor: "#FFFFFF", width: "100%", overflowX: "hidden" },
            }}
            disableEscapeKeyDown
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #ddd",
                backgroundColor: "#ECECEC",
                pb: 2,
                pt: 3,
                width: "100%",
            }}>
                <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: "27px", lineHeight: "100%", color: "#140F36", fontFamily: "Outfit, sans-serif" }}>
                        Edit Milestone
                    </Typography>
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={() => handleClose()}
                    sx={(theme) => ({
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    <RxCross2 style={{ fontSize: "30px", color: "#140F36" }} />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Formik
                            initialValues={formData}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ values,
                                errors,
                                touched,
                                handleChange,
                                handleBlur,
                                handleSubmit,
                                isSubmitting,
                                setFieldValue,
                                setFieldTouched,
                                setTouched, }) => (
                                <form onSubmit={handleSubmit}>
                                    <Grid container spacing={2}>

                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Typography sx={labelTextStyle}>Milestone Name</Typography>
                                            <TextField
                                                fullWidth
                                                name="milestone"
                                                value={values.milestone}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter Milestone Name"
                                                InputProps={{
                                                    sx: {
                                                        height: "40px",
                                                        borderRadius: "8px",
                                                        backgroundColor: "#fff",
                                                        "& input::placeholder": {
                                                            fontWeight: 400,
                                                            fontSize: "16px",
                                                            lineHeight: "100%",
                                                            letterSpacing: "0.16px",
                                                            color: "#0A0A0C",
                                                            fontFamily: "Outfit, sans-serif",
                                                            opacity: 1,
                                                        },
                                                        "& input": {
                                                            fontWeight: 400,
                                                            fontSize: "16px",
                                                            lineHeight: "100%",
                                                            letterSpacing: "0.16px",
                                                            color: "#0A0A0C",
                                                            fontFamily: "Outfit, sans-serif",
                                                        },
                                                    },
                                                }}
                                            />
                                            {touched.milestone && errors.milestone && (
                                                <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                    {errors.milestone}
                                                </Typography>
                                            )}
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Typography sx={labelTextStyle}>Milestone Value (INR Cr.)</Typography>
                                            <TextField
                                                fullWidth
                                                name="milestoneValue"
                                                value={values.milestoneValue}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter Milestone Value"
                                                InputProps={{
                                                    sx: {
                                                        height: "40px",
                                                        borderRadius: "8px",
                                                        backgroundColor: "#fff",
                                                        "& input::placeholder": {
                                                            fontWeight: 400,
                                                            fontSize: "16px",
                                                            lineHeight: "100%",
                                                            letterSpacing: "0.16px",
                                                            color: "#0A0A0C",
                                                            fontFamily: "Outfit, sans-serif",
                                                            opacity: 1,
                                                        },
                                                        "& input": {
                                                            fontWeight: 400,
                                                            fontSize: "16px",
                                                            lineHeight: "100%",
                                                            letterSpacing: "0.16px",
                                                            color: "#0A0A0C",
                                                            fontFamily: "Outfit, sans-serif",
                                                        },
                                                    },
                                                }}
                                            />
                                            {touched.milestoneValue && errors.milestoneValue && (
                                                <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                    {errors.milestoneValue}
                                                </Typography>
                                            )}
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                Due Date
                                            </Typography>
                                            <TextField
                                                type="date"
                                                name="dueDate"
                                                value={values.dueDate}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                onClick={(e) => {
                                                    const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                    if (input) input.showPicker?.();
                                                }}
                                                fullWidth
                                                sx={{
                                                    ...rightTextStyle,
                                                    "& input::-webkit-calendar-picker-indicator": {
                                                        display: "none",
                                                        WebkitAppearance: "none",
                                                    },
                                                    "& input[type='date']": {
                                                        appearance: "none",
                                                        WebkitAppearance: "none",
                                                    },
                                                    "& .MuiOutlinedInput-root": {
                                                        "& fieldset": {
                                                            border: "none",
                                                        },
                                                    },
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                            {CalenderSVG}
                                                        </Box>
                                                    ),
                                                    sx: {
                                                        backgroundColor: "#fff",
                                                        border: '1px solid',
                                                        borderColor: 'hsl(0, 0%, 80%)',
                                                        height: "40px",
                                                        width: { xs: "100%", md: "100%" },
                                                        borderRadius: "5px",
                                                    },
                                                }}
                                            />
                                            {touched.dueDate && errors.dueDate && (
                                                <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                    {errors.dueDate}
                                                </Typography>
                                            )}
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                Cure Period Due Date
                                            </Typography>
                                            <TextField
                                                type="date"
                                                name="curePeriodDueDate"
                                                value={values.curePeriodDueDate}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                onClick={(e) => {
                                                    const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                    if (input) input.showPicker?.();
                                                }}
                                                fullWidth
                                                sx={{
                                                    ...rightTextStyle,
                                                    "& input::-webkit-calendar-picker-indicator": {
                                                        display: "none",
                                                        WebkitAppearance: "none",
                                                    },
                                                    "& input[type='date']": {
                                                        appearance: "none",
                                                        WebkitAppearance: "none",
                                                    },
                                                    "& .MuiOutlinedInput-root": {
                                                        "& fieldset": {
                                                            border: "none",
                                                        },
                                                    },
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                            {CalenderSVG}
                                                        </Box>
                                                    ),
                                                    sx: {
                                                        backgroundColor: "#fff",
                                                        border: '1px solid',
                                                        borderColor: 'hsl(0, 0%, 80%)',
                                                        height: "40px",
                                                        width: { xs: "100%", md: "100%" },
                                                        borderRadius: "5px",
                                                    },
                                                }}
                                            />
                                            {touched.curePeriodDueDate && errors.curePeriodDueDate && (
                                                <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                    {errors.curePeriodDueDate}
                                                </Typography>
                                            )}
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Typography sx={labelTextStyle}>Status</Typography>
                                            <Select
                                                name="status"
                                                options={milestoneStatusOptions}
                                                value={milestoneStatusOptions.find(opt => opt.value === String(values.status)) || null}
                                                onChange={(option) => setFieldValue("status", option.value)}
                                                onBlur={() => setFieldTouched("status", true)}
                                                placeholder="Select Status"
                                            />
                                            {touched.status && errors.status && (
                                                <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                    {errors.status}
                                                </Typography>
                                            )}
                                        </Grid>

                                        <Grid size={12}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "flex-end",
                                                    alignItems: "center",
                                                    mb: 2,
                                                }}
                                            >
                                                <Button onClick={handleClose}
                                                    sx={{
                                                        textTransform: "none",
                                                        color: "#F15744",
                                                        fontSize: "14px",
                                                        lineHeight: "100%",
                                                        fontFamily: "Outfit, sans-serif",
                                                        borderRadius: "25px",
                                                        fontWeight: 500,
                                                        px: "20px",
                                                        py: "15px",
                                                    }}>
                                                    Cancel
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    // loading={isLoading}
                                                    sx={{
                                                        backgroundColor: "#00344C",
                                                        borderRadius: "25px",
                                                        textTransform: "none",
                                                        fontWeight: 500,
                                                        fontSize: "14px",
                                                        lineHeight: "100%",
                                                        fontFamily: "Outfit, sans-serif",
                                                        color: "#FFFFFF",
                                                        px: "20px",
                                                        py: "15px",
                                                    }}
                                                >
                                                    Save
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </form>
                            )}
                        </Formik>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default EditMilestone;